// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { X, Save, Loader2 } from "lucide-react";

const EditStatusForm = ({ status, isOpen, onClose, onSave, colorMap }) => {
  const [formData, setFormData] = useState({
    status_name: "",
    description: "",
    color_code: "",
    status_code: "",
    position: 0,
    is_active: true,
    is_system_status: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (status) {
      setFormData({
        status_name: status.status_name || "",
        description: status.description || "",
        color_code: status.color_code || "slate",
        status_code: status.status_code || "",
        position: status.position || 0,
        is_active: status.is_active !== undefined ? status.is_active : true,
        is_system_status: status.is_system_status || false,
      });
    }
  }, [status]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.status_name.trim()) {
      newErrors.status_name = "Status name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.status_code.trim()) {
      newErrors.status_code = "Status code is required";
    }

    if (formData.position < 0) {
      newErrors.position = "Position must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Error saving status:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex sm:items-center p-2 justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl sm:mx-4">
        <div className="flex items-center justify-between sm:p-6 p-4 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">
            Status bearbeiten
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="sm:p-6 p-4 space-y-4 overflow-y-auto h-[calc(100%-69px)]"
        >
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Statusname
            </label>
            <input
              type="text"
              name="status_name"
              value={formData.status_name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.status_name ? "border-red-300" : "border-slate-300"
              }`}
              placeholder="Enter status name"
            />
            {errors.status_name && (
              <p className="text-red-500 text-sm mt-1">{errors.status_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Beschreibung
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.description ? "border-red-300" : "border-slate-300"
              }`}
              placeholder="Enter description"
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Statuscode
            </label>
            <input
              type="text"
              name="status_code"
              value={formData.status_code}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.status_code ? "border-red-300" : "border-slate-300"
              }`}
              placeholder="Enter status code"
              disabled={formData.is_system_status}
            />
            {errors.status_code && (
              <p className="text-red-500 text-sm mt-1">{errors.status_code}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Farbcode
            </label>
            <select
              name="color_code"
              value={formData.color_code}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.keys(colorMap).map((color) => (
                <option key={color} value={color}>
                  {color.charAt(0).toUpperCase() + color.slice(1)}
                </option>
              ))}
            </select>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.keys(colorMap).map((color) => (
                <div
                  key={color}
                  className={`w-6 h-6 rounded-full border-2 cursor-pointer ${
                    formData.color_code === color
                      ? "border-slate-800"
                      : "border-slate-300"
                  } ${colorMap[color]}`}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, color_code: color }))
                  }
                  title={color}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Position (Sortierung)
            </label>
            <input
              type="number"
              name="position"
              value={formData.position}
              onChange={handleInputChange}
              min="0"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.position ? "border-red-300" : "border-slate-300"
              }`}
              placeholder="Enter position"
            />
            {errors.position && (
              <p className="text-red-500 text-sm mt-1">{errors.position}</p>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Aktiv</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_system_status"
                checked={formData.is_system_status}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-slate-700">Systemstatus</span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-rose-700 bg-rose-100 hover:bg-rose-200 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 border border-rose-200"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Aktualisieren
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStatusForm;
